import React, { useMemo } from "react";
import { arrayOf, object, oneOf, bool, number } from "prop-types";
import getConfig from "next/config";
import { useRouter } from "next/router";
import api from "@api";

import Typography from "@components/shared/Typography";

import { useGetFormattedPrice } from "@hooks/useGetFormattedPrice";
import getPriceAlgorithm from "@shared/PriceAlgorithm/getPriceAlgorithm";
import { dealStatusAllowsPromoCodeChanges } from "@shared/helper/dealStatusAllowsPromoCodeChanges";
import { isIndexOrConfigPage } from "@shared/helper/isIndexOrConfigPage";

const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

import {
  COUNTRIES,
  CTA_SOL_TYPES,
  DEAL_TYPE,
  JOURNEY_TYPE,
  SESSION_STORAGE_KEYS,
} from "@shared/constants";
import { getIsCountry } from "lib/getIsCountry";
import { dealType } from "types";
import { useDispatch } from "react-redux";
import {
  calculatePricesAction,
  postDealAction,
  removePromoCodeAction,
} from "redux/actions/dealActions";
import CloseIcon from "components/shared/Icons/Close";
import { useTranslation } from "@lib/i18n";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { sessionStorageClient } from "utils/sessionStorageClient";
import { twMerge } from "tailwind-merge";
import CheckmarkIcon from "@components/shared/Icons/Checkmark";
import Icon from "@components/shared/Icons/Icon";

export const DISPLAY_BLOCKS_VARIANT = {
  COMPACT: "COMPACT",
  WORDY: "WORDY",
  DEFAULT: "DEFAULT",
};

export const getDisplayBlocksVariant = journey => {
  switch (journey) {
    case JOURNEY_TYPE.LOA:
      return DISPLAY_BLOCKS_VARIANT.COMPACT;

    case JOURNEY_TYPE.VAC:
      return DISPLAY_BLOCKS_VARIANT.WORDY;

    case JOURNEY_TYPE.LLD:
    default:
      return DISPLAY_BLOCKS_VARIANT.DEFAULT;
  }
};

const CLASSNAMES = {
  DISPLAY_BLOCK_CONTAINER:
    "py-4 block border-b-2 border-b-greyLight last:border-b-0",
  DISPLAY_BLOCK: "flex justify-between w-full",
};

const FinanceDisplayBlocks = ({
  deal,
  displayBlocks,
  variant,
  isReadOnly,
  isQuantityVisible,
  vehicleQuantity,
}) => {
  const dispatch = useDispatch();
  const priceAlgorithm = getPriceAlgorithm(COUNTRY);
  const getFormattedPrice = useGetFormattedPrice();
  const isGB = getIsCountry(COUNTRIES.GB);
  const isPT = getIsCountry(COUNTRIES.PT);
  const isUserTrusted = useIsUserTrustedSelector();
  const { t } = useTranslation();
  const router = useRouter();

  const parsedFinanceDisplayBlocks = useMemo(() => {
    if (!displayBlocks) {
      return [];
    }

    const sortByOrder = (a, b) => (a.order > b.order ? 1 : -1);

    return [...displayBlocks]
      .sort(sortByOrder)
      .map(block => [...block.displayLines].sort(sortByOrder));
  });

  const { financeSimulation = { promoCode: undefined }, status } = deal ?? {};

  const { promoCode } = financeSimulation;

  const removePromoCode = async () => {
    if (!isUserTrusted) {
      api.removePromoCodeFromSession();
    }

    dispatch(removePromoCodeAction());
    dispatch(calculatePricesAction());

    sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);

    if (deal?.currentDeal?.token) {
      await dispatch(
        postDealAction(
          CTA_SOL_TYPES.CONFIGURATION_SAVED_BY_USER,
          DEAL_TYPE.DEAL,
          null,
          true
        )
      );
    }
  };

  const isCompactorDefault = [
    DISPLAY_BLOCKS_VARIANT.COMPACT,
    DISPLAY_BLOCKS_VARIANT.DEFAULT,
  ].includes(variant);

  return (
    <div
      className={twMerge(
        isCompactorDefault && CLASSNAMES.DISPLAY_BLOCK_CONTAINER
      )}
    >
      {!isIndexOrConfigPage(router.pathname) && promoCode?.valid && (
        <div className={CLASSNAMES.DISPLAY_BLOCK_CONTAINER}>
          <div className={CLASSNAMES.DISPLAY_BLOCK}>
            <Typography
              className={twMerge(
                "pt-1",
                variant === DISPLAY_BLOCKS_VARIANT.WORDY && "text-sm"
              )}
            >
              {promoCode.label}
              {!isReadOnly && dealStatusAllowsPromoCodeChanges(status) && (
                <button
                  className="inline-grid place-items-center ml-3 size-5 cursor-pointer transition duration-150 hover:text-red hover:scale-110 active:scale-95"
                  onClick={removePromoCode}
                  title={t("promoCodes.remove")}
                >
                  <CloseIcon size="sm" />
                </button>
              )}
            </Typography>
            <Typography
              className={twMerge(
                "pt-1",
                variant === DISPLAY_BLOCKS_VARIANT.WORDY &&
                  "text-sm whitespace-nowrap"
              )}
              data-id={`financefinancing-price-promoCode`}
            >
              {getFormattedPrice(promoCode.discountAmount * -1)}
            </Typography>
          </div>
        </div>
      )}

      {parsedFinanceDisplayBlocks.map((block, blockIndex) => (
        <div className={CLASSNAMES.DISPLAY_BLOCK_CONTAINER} key={blockIndex}>
          {block.map((blockItem, blockItemIndex) => (
            <div className={CLASSNAMES.DISPLAY_BLOCK} key={blockItemIndex}>
              <Typography
                className={twMerge(
                  blockItem.styleType?.indent && "pl-4",
                  variant === DISPLAY_BLOCKS_VARIANT.WORDY && "text-sm"
                )}
              >
                {variant === DISPLAY_BLOCKS_VARIANT.COMPACT &&
                  blockItem.styleType?.indent && (
                    <Icon
                      className="inline-block align-bottom mr-1 size-6"
                      icon={<CheckmarkIcon checked />}
                    />
                  )}
                {blockItem.label}
              </Typography>
              {blockItem.displayValue && (
                <Typography
                  className={twMerge(
                    "whitespace-nowrap",
                    variant === DISPLAY_BLOCKS_VARIANT.WORDY &&
                      "text-sm whitespace-nowrap"
                  )}
                  data-id={`financefinancing-price-${blockItemIndex}`}
                >
                  {/* Magic index values used to avoid formatting */}
                  {/* of non-currency values, needs refactoring */}
                  {isQuantityVisible && ![0, 3].includes(blockIndex)
                    ? getFormattedPrice(
                        priceAlgorithm.times(
                          Number(blockItem.value),
                          vehicleQuantity
                        )
                      )
                    : blockIndex === 0 &&
                      blockItemIndex === 1 &&
                      blockItem.value &&
                      !isGB && //APP-25320: A corner case for IT, when this clause is true, yet the 'blockItem' object only has 'displayItemValue' property and the 'value' property is undefined
                      !isPT
                    ? getFormattedPrice(
                        Number(blockItem.value) * vehicleQuantity
                      )
                    : blockItem.displayValue}
                </Typography>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

FinanceDisplayBlocks.propTypes = {
  displayBlocks: arrayOf(object).isRequired,
  variant: oneOf(Object.values(DISPLAY_BLOCKS_VARIANT)).isRequired,
  isReadOnly: bool,
  isQuantityVisible: bool,
  vehicleQuantity: number,
  deal: dealType,
};

export default FinanceDisplayBlocks;
